/** Plan prices for the person looking (signed-in business, visitor, or a picked country). */
import useAuthStore from "@/stores/authStore";
import { COUNTRIES, useCurrencyStore } from "@/lib/currency";
import { useFxRates } from "@/hooks/useFxRates";
import { useVisitorLocale } from "@/lib/visitor-locale";
import { usePublicPlans } from "@/hooks/usePublicPlans";
import type { BillingCycle } from "@/constants/plans";
import { billingCurrencyFor, resolvePlanPrice, type PricedPlan } from "@/lib/plan-pricing";

/**
 * Prices for this person. `override` (country or currency) wins — sign-up
 * passes the country they picked, since that's what they'll be billed in.
 */
export function usePlanPricing(override?: { country?: string | null; currency?: string | null }) {
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  const businessCode = useCurrencyStore((s) => s.code);
  const needVisitor = !signedIn && !override?.country && !override?.currency;
  const visitor = useVisitorLocale(undefined, needVisitor);
  const { chargeable, plans } = usePublicPlans();
  const fromCountry = override?.country ? COUNTRIES.find((c) => c.code === override.country)?.currency : null;
  const local = String(override?.currency || fromCountry || (signedIn ? businessCode : visitor.data?.currency) || "GHS").toUpperCase();
  const fx = useFxRates("GHS", local !== "GHS");
  const visitorRate = visitor.data?.currency === local ? visitor.data?.planRate ?? null : null;
  const rate = local === "GHS" ? 1 : fx.data?.rates?.[local] ?? visitorRate;
  return {
    local,
    billing: billingCurrencyFor(local, chargeable),
    chargeable,
    plans,
    price: (plan: PricedPlan, cycle: BillingCycle) => resolvePlanPrice(plan, cycle, local, chargeable, rate),
  };
}

